import React, { useState, useRef } from "react";

const SpeechButton = ({ text, disabled = false }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const handleSpeech = async () => {
    if (isPlaying && audioRef.current) {
      // If already playing, stop the audio
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      return;
    }

    setIsLoading(true);

    try {
      const options = {
        method: "POST",
        headers: {
          // Authorization:
          //   "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOiI2NzVhOTg4N2FiMThmODE5OGE1NTkzNDUiLCJ0eXBlIjoiYXBpS2V5IiwiaWF0IjoxNzMzOTkwNTM1LCJleHAiOjQ4ODk3NTA1MzV9.JD0MNTurZNkorKB4vYdL0btyZKy-npR27FKSoKVaBdU",
          "Content-Type": "application/json",
          "xi-api-key": process.env.REACT_APP_ELEVENLABS_API,
        },
        body: JSON.stringify({
          text: text,
          model_id: "eleven_multilingual_v2",
          voice_id: "zT03pEAEi0VHKciJODfn",
          // voice_id: "karan",
          // add_wav_header: true,
          // sample_rate: 16000,
          // speed: 1,
          // language: "en",
        }),
      };

      // First, try to get the response as JSON to check if there's an error message
      const response = await fetch(
        "https://api.elevenlabs.io/v1/text-to-speech/JBFqnCBsd6RMkjVDRZzb?output_format=mp3_44100_128",
        options
      );

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      // Clone the response to use it twice
      const responseClone = response.clone();

      try {
        // Try to parse as JSON first to see if it's an error response
        const jsonData = await responseClone.json();
        console.log("API response:", jsonData);

        if (jsonData.error) {
          throw new Error(`API error: ${jsonData.error}`);
        }

        // If we have audio_base64, use that
        if (jsonData.audio_base64) {
          const audioSrc = `data:audio/wav;base64,${jsonData.audio_base64}`;

          if (!audioRef.current) {
            audioRef.current = new Audio(audioSrc);

            audioRef.current.addEventListener("ended", () => {
              setIsPlaying(false);
            });

            audioRef.current.addEventListener("error", (e) => {
              console.error("Audio playback error:", e);
              setIsPlaying(false);
            });
          } else {
            audioRef.current.src = audioSrc;
          }

          await audioRef.current.play();
          setIsPlaying(true);
          return;
        }
      } catch (jsonError) {
        // Not JSON, so it's probably binary data
        console.log("Not JSON response, trying as binary data");
      }

      // If we get here, try to handle as binary data
      const arrayBuffer = await response.arrayBuffer();

      // Create a Blob with the correct MIME type
      const blob = new Blob([arrayBuffer], { type: "audio/wav" });

      // Create a URL for the blob
      const audioUrl = URL.createObjectURL(blob);

      console.log("Created audio URL:", audioUrl);

      if (audioRef.current) {
        // Clean up previous objectURL
        if (audioRef.current.src && audioRef.current.src.startsWith("blob:")) {
          URL.revokeObjectURL(audioRef.current.src);
        }
        audioRef.current.src = audioUrl;
      } else {
        audioRef.current = new Audio(audioUrl);

        audioRef.current.addEventListener("ended", () => {
          setIsPlaying(false);
        });

        audioRef.current.addEventListener("error", (e) => {
          console.error("Audio playback error:", e);
          setIsPlaying(false);
        });
      }

      // Add a debug log before playing
      console.log("About to play audio from URL:", audioRef.current.src);

      // Try to play the audio
      try {
        await audioRef.current.play();
        setIsPlaying(true);
      } catch (playError) {
        console.error("Failed to play audio:", playError);
        throw new Error("Failed to play audio");
      }
    } catch (error) {
      console.error("Error generating speech:", error);
      alert("Failed to generate speech. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleSpeech}
      disabled={disabled || isLoading || !text}
      className="speech-button"
      title={isPlaying ? "Stop speaking" : "Listen to this message"}
      style={{
        background: "none",
        border: "none",
        cursor: disabled || isLoading ? "not-allowed" : "pointer",
        opacity: disabled || isLoading || !text ? 0.5 : 1,
        padding: "4px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {isLoading ? (
        // Loading spinner
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="rotating-icon"
        >
          <path
            d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12"
            stroke="#ADACBF"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : isPlaying ? (
        // Stop icon
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="6" y="6" width="12" height="12" fill="#ADACBF" />
        </svg>
      ) : (
        // Speaker icon
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3 9V15H7L12 20V4L7 9H3Z"
            stroke="#ADACBF"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M16.5 12C16.5 10.23 15.48 8.71 14 7.97V16.02C15.48 15.29 16.5 13.77 16.5 12Z"
            stroke="#ADACBF"
            strokeWidth="2"
            fill="none"
          />
        </svg>
      )}
    </button>
  );
};

export default SpeechButton;
